import React, { useCallback, useMemo } from "react";
import { View, Text, Image, Linking, Pressable, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import { faCalendar, faTree, faMessage, faDollarSign, faLink, faX } from "@fortawesome/free-solid-svg-icons";
import { tabIconSize, FontAwesomeIcon, logAnalyticsEvent, isLargeScreen, os, platform, logError } from "../platform";
import SEOHeaderTags from "../components/SEOHeaderTags";
import { COLORS } from "../theme/colors";
import { STRINGS } from "../constants/strings";
import AppStore from "../storage/appStore.js";
import Home from "../pages/Home";
import Links from "../pages/Links";
import Settings from "../pages/Settings";
import Terms from "../pages/Terms";
import WebViewScreen from "../pages/WebViewScreen";
import ChatBot from "../components/ChatBot";
import HeaderBackButton from "../components/HeaderBackButton";
import SocialMediaDropdown from "../components/SocialMediaDropdown";
import HeaderWebView from "../components/HeaderWebView";

const MainTab = createBottomTabNavigator();

/**
 * Navigation theme configuration
 * @constant
 */
const NAVIGATION_THEME = {
  colors: {
    primary: COLORS.primary,
    background: COLORS.background,
    card: COLORS.surface,
    text: COLORS.textDark,
    border: COLORS.border,
    notification: COLORS.secondary,
  },
};

/**
 * Tab bar height configuration by platform
 * @constant
 */
const TAB_BAR_HEIGHT = {
  web: 60,
  ios: 75,
  android: 70,
};

/**
 * Chat popover configuration for web platform
 * @constant
 */
const POPOVER_CONFIG = {
  height: 554,
  maxWidth: 520,
  minHeight: 300,
  bottomOffset: 100,
};

/**
 * Social media icons configuration for header display
 * @constant
 * @type {Array<{name: string, asset: any, label: string, key: string}>}
 */
const SOCIAL_ICONS = [
  { name: "beintheknow", asset: require("../assets/beintheknow.webp"), label: "Be in the Know", key: "beintheknow" },
  { name: "twitter", asset: require("../assets/x.webp"), label: "Twitter", key: "twitter" },
  { name: "facebook", asset: require("../assets/fb.webp"), label: "Facebook", key: "facebook" },
  { name: "linkedin", asset: require("../assets/linkedin.webp"), label: "LinkedIn", key: "linkedin" },
  { name: "youtube", asset: require("../assets/youtube.webp"), label: "YouTube", key: "youtube" },
  { name: "instagram", asset: require("../assets/instagram.webp"), label: "Instagram", key: "instagram" },
];

// Memoize CustomTabBarButton to prevent unnecessary re-renders
const CustomTabBarButton = React.memo(
  ({ children, accessibilityState, showPopover, setShowPopover, navigation, isChatBotActive }) => {
    /**
     * Handles chat button press.
     * On native: navigates to ChatBot screen or goes back if already on ChatBot.
     * On web: toggles chat popover and logs analytics.
     */
    const handlePress = useCallback(() => {
      if (platform === "native") {
        if (isChatBotActive) {
          navigation.goBack();
        } else {
          navigation.navigate("ChatBot");
          logAnalyticsEvent("chatbot_opened");
        }
      } else {
        setShowPopover(!showPopover);
        if (!showPopover) {
          logAnalyticsEvent("chatbot_opened");
        }
      }
    }, [navigation, showPopover, setShowPopover, isChatBotActive]);

    // Memoize backdrop press handler
    const handleBackdropPress = useCallback(() => {
      setShowPopover(false);
    }, [setShowPopover]);

    // Memoize android_ripple config
    const rippleConfig = useMemo(
      () => ({
        borderless: true,
        color: "transparent",
      }),
      []
    );

    // Memoize button style
    const buttonStyle = useMemo(
      () => [styles.customButton, (isChatBotActive || showPopover) && styles.glowButton],
      [isChatBotActive, showPopover]
    );

    return (
      <>
        <Pressable
          id="chat-button"
          style={styles.customTabBarButton}
          onPress={handlePress}
          activeOpacity={0.8}
          android_ripple={rippleConfig}
          accessibilityLabel={isChatBotActive || showPopover ? "Close Chat Support" : "Open Chat Support"}
          accessibilityRole="button"
          accessibilityHint="Toggles the chat support window"
        >
          <View style={buttonStyle}>{children}</View>
        </Pressable>
        {platform === "web" && showPopover && (
          <>
            <Pressable style={styles.chatBackdrop} onPress={handleBackdropPress} testID="chat-backdrop" />
            <View style={styles.chatPopover}>
              <ChatBot isModal={true} />
            </View>
          </>
        )}
      </>
    );
  }
);
CustomTabBarButton.displayName = "CustomTabBarButton";

// Memoize HeaderLeftLogo to prevent unnecessary re-renders
const HeaderLeftLogo = React.memo(() => {
  return (
    <View style={styles.headerContainer}>
      <Image source={require("../assets/vbconnect_logo_orange.webp")} style={styles.headerImage} resizeMode="contain" />
      <SEOHeaderTags style={styles.headerTextSun} text={STRINGS.connect} tag="h1" />
    </View>
  );
});
HeaderLeftLogo.displayName = "HeaderLeftLogo";

/**
 * Header right component displaying social media icons.
 * Renders clickable icons that link to VB social media pages.
 * Uses custom dropdown for overflow icons.
 *
 * @component
 * @returns {JSX.Element} Container with social media icon links
 */
const HeaderRightSocialIcons = React.memo(() => {
  const navigation = useNavigation();
  const setErrorMessage = AppStore((state) => state.setErrorMessage);
  const socialLinks = STRINGS.socialLinks;

  const handleIconPress = useCallback(
    async (icon) => {
      try {
        if (os === "web") {
          await Linking.openURL(socialLinks[icon.key]);
        } else {
          navigation.navigate("WebViewScreen", { url: socialLinks[icon.key], title: icon.label });
        }
      } catch (error) {
        logError("link_open_failed", {
          message: error.message,
          stack: error.stack,
          url: socialLinks[icon.key],
          name: icon.name,
        });
        setErrorMessage(STRINGS.failedToOpenLink);
      }
    },
    [socialLinks, navigation, setErrorMessage]
  );

  return <SocialMediaDropdown icons={SOCIAL_ICONS} onIconPress={handleIconPress} />;
});

HeaderRightSocialIcons.displayName = "HeaderRightSocialIcons";

/**
 * Empty component for tabs that don't render content (external links)
 *
 * @component
 * @returns {null}
 */
const EmptyComponent = () => null;

const TabBarIcon = React.memo(({ routeName, color, showPopover, isChatBotActive }) => {
  let iconName;
  let iconColor = color;

  switch (routeName) {
    case "Home":
      iconName = showPopover || isChatBotActive ? faX : faMessage;
      iconColor = "#ffffff";
      break;
    case "Events":
      iconName = faCalendar;
      break;
    case "Parks":
      iconName = faTree;
      break;
    case "PayBills":
      iconName = faDollarSign;
      break;
    case "Links":
      iconName = faLink;
      break;
    default:
      iconName = faLink;
      break;
  }

  return <FontAwesomeIcon icon={iconName} size={tabIconSize} color={iconColor} />;
});

// Memoized tab label component
const TabItemText = React.memo(({ title, color, focused }) => (
  <Text
    style={[
      styles.tabItemText,
      { color },
      focused && styles.tabItemTextFocused,
      isLargeScreen && styles.tabItemTextLarge,
      os === "android" && styles.tabItemTextAndroid,
    ]}
    maxFontSizeMultiplier={1.2}
  >
    {title}
  </Text>
));
TabItemText.displayName = "TabItemText";

// Memoized header title component
const TabHeaderTitle = React.memo(({ title }) => (
  <View>
    <SEOHeaderTags style={styles.headerTitle} text={title} tag="h1" />
  </View>
));
TabHeaderTitle.displayName = "TabHeaderTitle";

const NativeWebviewHeaderTitle = React.memo(({ title }) => (
  <View>
    <Text style={styles.nativeWebviewHeaderTitle} maxFontSizeMultiplier={1}>
      {title}
    </Text>
  </View>
));
NativeWebviewHeaderTitle.displayName = "NativeWebviewHeaderTitle";

/**
 * Screen listeners for analytics tracking
 *
 * @param {Function} setIsChatBotActive - Function to update ChatBot active state
 * @param {Function} setPageName - Function to update current page name in state
 * @returns {Object} Navigation listeners configuration
 */
const screenListeners = (setIsChatBotActive, setPageName) => ({
  state: (e) => {
    const currentRoute = e.data.state.routes[e.data.state?.index];
    setPageName(currentRoute.name);
    logAnalyticsEvent("screen_view", { name: currentRoute.name });
    setIsChatBotActive(currentRoute.name === "ChatBot");
  },
});

const RootNavigator = () => {
  const setPageName = AppStore.getState().setPageName;
  const [showPopover, setShowPopover] = React.useState(false);
  const [isChatBotActive, setIsChatBotActive] = React.useState(false);
  const navigationTheme = useMemo(() => NAVIGATION_THEME, []);

  // Memoize tab press handler for closing popover
  const handleTabPress = useCallback(() => {
    setShowPopover(false);
  }, []);

  // Memoize external app press handler to prevent unnecessary re-renders
  const handleExternalAppPress = useCallback(
    (appUrl, fallbackUrl, appName, navigation) => async (e) => {
      setShowPopover(false);
      e.preventDefault();

      // On web, always use the fallback URL in external browser
      if (platform === "web") {
        try {
          await Linking.openURL(fallbackUrl);
        } catch (webError) {
          console.error(`Failed to open ${appName}:`, webError);
        }
        return;
      }

      // On native, try to open app directly
      try {
        await Linking.openURL(appUrl);
      } catch (error) {
        // If app is not installed or fails to open, fall back to WebViewScreen
        try {
          navigation.navigate("WebViewScreen", { url: fallbackUrl, title: appName });
        } catch (navError) {
          console.error(`Failed to navigate to ${appName}:`, navError);
        }
      }
    },
    []
  );

  // Memoize Events tab press handler
  const handleEventsTabPress = useCallback(
    (navigation) => handleExternalAppPress(STRINGS.vbgoNativeLink, STRINGS.vbgoLink, "VBGO", navigation),
    [handleExternalAppPress]
  );

  // Memoize Parks tab press handler
  const handleParksTabPress = useCallback(
    (navigation) =>
      handleExternalAppPress(STRINGS.vbparkfinderNativeLink, STRINGS.vbparkfinderLink, "VB Park Finder", navigation),
    [handleExternalAppPress]
  );

  // Memoize screen options
  const screenOptions = useMemo(
    () =>
      ({ route }) => ({
        tabBarIcon: ({ color }) => (
          <TabBarIcon
            routeName={route.name}
            color={color}
            showPopover={showPopover}
            isChatBotActive={isChatBotActive}
          />
        ),
        tabBarActiveTintColor: COLORS.activeNavItem,
        tabBarInactiveTintColor: COLORS.text,
        tabBarLabelPosition: os === "web" ? "below-icon" : isLargeScreen ? "beside-icon" : "below-icon",
        tabBarStyle: os === "web" ? styles.webTabBar : styles.nativeTabBar,
        tabBarLabelStyle: os === "web" ? styles.webTabLabel : undefined,
        tabBarItemStyle: os === "web" ? styles.webTabItem : undefined,
      }),
    [isChatBotActive, showPopover]
  );

  const renderHeaderRight = useCallback(() => <HeaderRightSocialIcons />, []);

  const headerLeftLogo = useCallback(() => <HeaderLeftLogo />, []);

  const homeOptions = useCallback(
    ({ navigation }) => ({
      headerTitle: () => null,
      headerLeft: headerLeftLogo,
      headerRight: () => renderHeaderRight(),
      tabBarLabel: () => null,
      tabBarButton: (props) => (
        <CustomTabBarButton
          {...props}
          showPopover={showPopover}
          setShowPopover={setShowPopover}
          navigation={navigation}
          isChatBotActive={isChatBotActive}
        />
      ),
    }),
    [showPopover, renderHeaderRight, headerLeftLogo, isChatBotActive]
  );

  // Memoize Events screen listeners
  const eventsListeners = useCallback(
    ({ navigation }) => ({
      tabPress: handleEventsTabPress(navigation),
    }),
    [handleEventsTabPress]
  );

  // Memoize Parks screen listeners
  const parksListeners = useCallback(
    ({ navigation }) => ({
      tabPress: handleParksTabPress(navigation),
    }),
    [handleParksTabPress]
  );

  // Memoize tab press listeners
  const tabPressListeners = useMemo(
    () => ({
      tabPress: handleTabPress,
    }),
    [handleTabPress]
  );

  // Memoize PayBills screen options
  const payBillsOptions = useCallback(
    ({ navigation }) => ({
      headerShown: true,
      title: "Pay Bills",
      headerTitle: () => <TabHeaderTitle title="Pay Bills" />,
      headerTitleAlign: "center",
      tabBarLabel:
        platform === "native"
          ? ({ color, focused }) => <TabItemText title="Pay Bills" color={color} focused={focused} />
          : "Pay Bills",
      headerLeft: () => <HeaderBackButton navigation={navigation} />,
    }),
    []
  );

  const linksOptions = useCallback(
    ({ navigation }) => ({
      headerShown: true,
      title: "Links",
      headerTitle: () => <TabHeaderTitle title="Links" />,
      headerTitleAlign: "center",
      tabBarLabel:
        platform === "native"
          ? ({ color, focused }) => <TabItemText title="Links" color={color} focused={focused} />
          : "Links",
      headerLeft: () => <HeaderBackButton navigation={navigation} />,
    }),
    []
  );

  // Memoize Settings screen options
  const settingsOptions = useCallback(
    ({ navigation }) => ({
      headerShown: true,
      title: "Settings",
      headerTitle: () => <TabHeaderTitle title="Settings" />,
      headerTitleAlign: "center",
      tabBarButton: () => null,
      headerLeft: () => <HeaderBackButton navigation={navigation} />,
      headerRight: () => null,
    }),
    []
  );

  // Memoize Terms screen options
  const termsOptions = useCallback(
    ({ navigation }) => ({
      headerShown: true,
      title: "Privacy Policy",
      headerTitle: () => <TabHeaderTitle title="Privacy Policy" />,
      headerTitleAlign: "center",
      tabBarButton: () => null,
      headerLeft: () => <HeaderBackButton navigation={navigation} />,
      headerRight: () => null,
    }),
    []
  );

  // Memoize ChatBot screen options
  const chatBotOptions = useCallback(
    ({ navigation }) => ({
      headerShown: true,
      title: "Chat Support",
      headerTitle: () => <TabHeaderTitle title="Chat Support" />,
      headerTitleAlign: "center",
      tabBarButton: () => null,
      headerLeft: () => <HeaderBackButton navigation={navigation} />,
      unmountOnBlur: true,
    }),
    []
  );

  const webviewOptions = useCallback(
    ({ navigation, route }) => ({
      headerShown: true,
      headerTitle: () => <NativeWebviewHeaderTitle title={route.params?.title || ""} />,
      tabBarButton: () => null,
      headerLeft: () => <HeaderBackButton navigation={navigation} />,
      headerRight: () => <HeaderWebView url={route.params?.url} title={route.params?.title} />,
      unmountOnBlur: true,
    }),
    []
  );

  // Memoize Events screen options
  const eventsOptions = useMemo(
    () => ({
      headerShown: true,
      title: platform === "native" ? () => <TabHeaderTitle title="Events" /> : "Events",
      tabBarLabel:
        platform === "native"
          ? ({ color, focused }) => <TabItemText title="Events" color={color} focused={focused} />
          : "Events",
    }),
    []
  );

  // Memoize Parks screen options
  const parksOptions = useMemo(
    () => ({
      headerShown: true,
      title: platform === "native" ? () => <TabHeaderTitle title="Parks" /> : "Parks",
      tabBarLabel:
        platform === "native"
          ? ({ color, focused }) => <TabItemText title="Parks" color={color} focused={focused} />
          : "Parks",
    }),
    []
  );
  const routes = useMemo(
    () => [
      {
        name: "Events",
        component: EmptyComponent,
        options: eventsOptions,
        listeners: eventsListeners,
      },
      {
        name: "Parks",
        component: EmptyComponent,
        options: parksOptions,
        listeners: parksListeners,
      },
      {
        name: "Home",
        component: Home,
        options: homeOptions,
        listeners: tabPressListeners,
      },
      {
        name: "PayBills",
        component: Links,
        options: payBillsOptions,
        listeners: tabPressListeners,
      },
      {
        name: "Links",
        component: Links,
        options: linksOptions,
        listeners: tabPressListeners,
      },
      {
        name: "Settings",
        component: Settings,
        options: settingsOptions,
        listeners: tabPressListeners,
      },
      {
        name: "Terms",
        component: Terms,
        options: termsOptions,
        listeners: tabPressListeners,
      },
      {
        name: "ChatBot",
        component: ChatBot,
        options: chatBotOptions,
      },
      {
        name: "WebViewScreen",
        component: WebViewScreen,
        options: webviewOptions,
      },
    ],
    [
      eventsOptions,
      parksOptions,
      homeOptions,
      payBillsOptions,
      linksOptions,
      settingsOptions,
      termsOptions,
      chatBotOptions,
      webviewOptions,
      eventsListeners,
      parksListeners,
      tabPressListeners,
    ]
  );

  const memoizedScreenListeners = useMemo(
    () => screenListeners(setIsChatBotActive, setPageName),
    [setIsChatBotActive, setPageName]
  );

  return (
    <MainTab.Navigator
      initialRouteName="Home"
      backBehavior="history"
      screenOptions={screenOptions}
      screenListeners={memoizedScreenListeners}
      theme={navigationTheme}
    >
      {routes.map((route) => (
        <MainTab.Screen
          key={route.name}
          name={route.name}
          component={route.component}
          options={route.options}
          listeners={route.listeners}
        />
      ))}
    </MainTab.Navigator>
  );
};

const styles = StyleSheet.create({
  webTabBar: {
    height: 60,
    paddingBottom: 7,
    borderTopWidth: 0.5,
    ...(os === "web"
      ? {
          boxShadow: "0px 5px 5px rgba(0, 0, 0, 0.15)",
        }
      : {
          shadowOffset: { width: 0, height: 5 },
          shadowOpacity: 0.15,
          shadowRadius: 5,
          elevation: 5,
        }),
  },
  webTabLabel: {
    fontSize: 12,
    marginTop: -2,
  },
  webTabItem: {
    paddingTop: 8,
  },
  nativeTabBar: {
    height: os === "ios" ? 75 : 70,
    backgroundColor: COLORS.background,
    paddingBottom: os === "ios" ? 20 : 10,
    paddingTop: os === "ios" ? 10 : 5,
    borderTopColor: COLORS.border,
    borderTopWidth: 0.5,
    ...(os === "web"
      ? {
          boxShadow: "0px 5px 5px rgba(0, 0, 0, 0.15)",
        }
      : {
          elevation: 20,
          shadowColor: COLORS.uiGray900,
          shadowOffset: { width: 0, height: 5 },
          shadowOpacity: 0.15,
          shadowRadius: 5,
        }),
  },
  customTabBarButton: {
    top: os === "web" ? -35 : -30,
    justifyContent: "center",
    alignItems: "center",
  },
  customButton: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: COLORS.red,
    justifyContent: "center",
    alignItems: "center",
    ...((os === "web" || os === "ios") && {
      boxShadow: "0px 10px 5px rgba(0, 0, 0, 0.3)",
    }),
  },
  glowButton: {
    borderWidth: 0,
    borderColor: COLORS.glowButtonBorder,
    ...(os === "web"
      ? {
          boxShadow: `0px 0px 8px 0px ${COLORS.uiGray900}`,
        }
      : {
          shadowColor: COLORS.glowButtonBorder,
          shadowOffset: { width: 0, height: 0 },
          shadowOpacity: 1,
          shadowRadius: 8,
        }),
    elevation: 20,
    ...(os === "android" && {
      borderWidth: 1.5,
      borderColor: COLORS.glowButtonBorder,
      shadowColor: COLORS.glowButtonBorder,
      elevation: 10,
    }),
  },
  headerTitle: {
    fontSize: 18,
  },
  nativeWebviewHeaderTitle: {
    fontSize: 18,
    textAlign: "center",
  },
  settingsButton: {
    paddingRight: 15,
  },
  headerContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  headerImage: {
    width: os === "web" ? 50 : 40,
    height: os === "web" ? 50 : 40,
    marginLeft: 10,
  },
  headerText: {
    fontSize: 25,
    marginLeft: 8,
    color: COLORS.primary,
  },
  headerTextSun: {
    fontSize: 25,
    marginLeft: 8,
    color: COLORS.vbSun,
  },
  tabItemText: {
    fontSize: 12,
    fontWeight: "400",
  },
  tabItemTextAndroid: {
    marginTop: -10,
  },
  tabItemTextFocused: {
    fontWeight: "600",
  },
  tabItemTextLarge: {
    marginLeft: 20,
  },
  backButton: {
    padding: 10,
  },
  socialIcon: {
    width: 24,
    height: 24,
    marginHorizontal: 5,
  },
  socialIconsContainer: {
    flexDirection: "row",
    marginRight: 15,
  },
  chatBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: TAB_BAR_HEIGHT.web,
    backgroundColor: "transparent",
    zIndex: 9998,
  },
  chatPopover: {
    position: "absolute",
    width: "92%",
    height: POPOVER_CONFIG.height,
    maxWidth: POPOVER_CONFIG.maxWidth,
    maxHeight: POPOVER_CONFIG.height,
    minHeight: POPOVER_CONFIG.minHeight,
    padding: 0,
    margin: 0,
    left: "50%",
    bottom: POPOVER_CONFIG.bottomOffset,
    zIndex: 9999,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 10,
    ...(os === "web"
      ? {
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
        }
      : {
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 0 },
          shadowOpacity: 0.1,
          shadowRadius: 10,
        }),
    transform: [{ translateX: "-50%" }],
  },
});

export default RootNavigator;
